import { useMemo } from 'react';

import Icon from 'components/global/Icon';
import Skeleton from 'components/global/Skeleton';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import { faArrowRight, faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

import * as S from './styled/DataCards.styled';

/**
 * This is a representational component that will render a single DataCard component with icon, label, value and action button
 * @param {object} props - props of the component
 * @param { boolean } props.loading - whether data is loading
 * @param { object } props.cardConfig - contains information about the element that the card will represent, including icon, label, color, background color, etc.
 * @param { object } props.data - data of a single item that comes from the fetch request
 * @returns {JSX.Element}
 */

export const DataCard = ({ loading, cardConfig, data, withCurrency, currency }) => {
	const { label, icon, customIcon, backgroundColor, color, onButtonClick, tooltip } = cardConfig;

	return (
		<S.DataCard onClick={onButtonClick} isLoading={loading}>
			<S.CardContentWrapper>
				<S.IconVariation
					color={color}
					backgroundColor={backgroundColor}
					size="large"
					icon={icon}
					kitName={customIcon}
				/>
				<div>
					<Typography noWrap variant="p2">
						{label}
					</Typography>
					{loading ? (
						<Skeleton width="5rem" height="2.2rem" />
					) : (
						<Typography
							css={S.Value}
							variant="h4"
							fontWeight="700"
							currency={currency}
							withCurrency={withCurrency}>
							{data}
						</Typography>
					)}
				</div>
				{tooltip && (
					<div>
						<Tooltip placement="right" title={tooltip}>
							<Icon icon={faCircleInfo} />
						</Tooltip>
					</div>
				)}
			</S.CardContentWrapper>
			{!loading && onButtonClick ? (
				<S.ViewLink>
					<Typography color="var(--blue)" variant="p3">
						view
					</Typography>
					<Icon icon={faArrowRight} color="var(--blue)" />
				</S.ViewLink>
			) : null}
		</S.DataCard>
	);
};

/**
 * This component will render multiple DataCard components
 * @param { string } props.fetchURL - URL to fetch data from
 * @param { object } props.body - body to send with the fetch request
 * @param { array } props.cardsConfig - array of objects that contain information about the elements that the cards will represent
 * @param { number } props.defaultValue - default value to show when data is not available
 * @returns {JSX.Element}
 */
const DataCards = (props) => {
	const { data, loading, cardsConfig, defaultValue = 0 } = props;

	const extractedData = useMemo(() => data && (data.constructor === Array ? data[0] : data), [data]);

	// if there is a response and there is an entry correlated with the accessor, return it; but if there is no such entry return undefined
	// if there is no response, return the default value
	const getData = (accessor) => {
		if (data) {
			return extractedData ? extractedData[accessor] : defaultValue;
		}
		return defaultValue;
	};

	return (
		<S.DataCardsWrapper>
			{cardsConfig.map(
				(cardConfig) =>
					getData(cardConfig.accessor) !== undefined && (
						<DataCard
							loading={loading}
							key={cardConfig.accessor}
							cardConfig={cardConfig}
							data={getData(cardConfig.accessor)}
							withCurrency={!!extractedData?.currency}
							currency={extractedData?.currency}
						/>
					)
			)}
		</S.DataCardsWrapper>
	);
};

export default DataCards;
